import React, { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Download, Loader2, Share2, Phone, MapPin, FileSignature, Users2, Building2, Lightbulb } from "lucide-react";

const NAVY = "#0F2C59";
const PRED_COLOR = { KESIN: "#16A34A", BELKI: "#F59E0B", GELMEYECEK: "#64748B", ULASILAMADI: "#0EA5E9", KARSI: "#DC2626", BOS: "#94A3B8" };
const PRED_LABEL = { KESIN: "Kesin", BELKI: "Belki", GELMEYECEK: "Gelmeyecek", ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", BOS: "İşaretsiz" };
const REASON = {
  person: { l: "Ortak kişi", icon: Users2 },
  phone: { l: "Aynı telefon", icon: Phone },
  address: { l: "Aynı adres", icon: MapPin },
  title: { l: "Unvan grubu", icon: Building2 },
  sicil: { l: "Aynı sicil kökü", icon: FileSignature },
};

const ReasonChips = ({ reasons = [] }) => (
  <span className="inline-flex flex-wrap gap-1">
    {reasons.slice(0, 3).map((r, i) => {
      const meta = REASON[r.type] || { l: r.type, icon: Share2 };
      const Icon = meta.icon;
      return (
        <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[11px]">
          <Icon className="h-3 w-3 opacity-70" />
          {meta.l}{r.label ? `: ${String(r.label).slice(0, 28)}` : ""}
        </span>
      );
    })}
  </span>
);

const Graph = ({ data, onOpenFirm }) => {
  const W = 900, H = 420, cx = W / 2, cy = H / 2;
  const pos = useMemo(() => {
    const p = { [data.root.voter_id]: [cx, cy] };
    const d1 = data.nodes.filter((n) => n.depth === 1);
    const d2 = data.nodes.filter((n) => n.depth === 2);
    d1.forEach((n, i) => {
      const a = (i / Math.max(d1.length, 1)) * Math.PI * 2 - Math.PI / 2;
      p[n.voter_id] = [cx + Math.cos(a) * 120, cy + Math.sin(a) * 100];
    });
    d2.forEach((n, i) => {
      const a = (i / Math.max(d2.length, 1)) * Math.PI * 2 - Math.PI / 2;
      p[n.voter_id] = [cx + Math.cos(a) * 200, cy + Math.sin(a) * 175];
    });
    return p;
  }, [data, cx, cy]);

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-muted/20" data-testid="fn-graph">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 640, height: 420 }}>
        {data.edges.map((e, i) => {
          const a = pos[e.source], b = pos[e.target];
          if (!a || !b) return null;
          return <line key={i} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="#94A3B8" strokeWidth={0.7} opacity={0.5} />;
        })}
        {data.nodes.map((n) => {
          const [x, y] = pos[n.voter_id] || [cx, cy];
          const c = PRED_COLOR[n.prediction] || PRED_COLOR.BOS;
          const r = n.depth === 0 ? 11 : n.depth === 1 ? 7 : 5;
          return (
            <g key={n.voter_id} style={{ cursor: "pointer" }} onClick={() => onOpenFirm?.(n.voter_id)}
              data-testid={`fn-node-${n.voter_id}`}>
              <title>{n.company_name}</title>
              <circle cx={x} cy={y} r={r} fill={c} stroke={n.depth === 0 ? NAVY : "#fff"} strokeWidth={n.depth === 0 ? 3 : 1.2} />
              {(n.depth === 0 || data.nodes.length <= 40) && (
                <text x={x} y={y - r - 4} textAnchor="middle" fontSize={n.depth === 0 ? 11 : 8.5} fill="currentColor">
                  {(n.company_name || "").slice(0, 26)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export const FirmNetwork = ({ canExport, onOpenFirm }) => {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [current, setCurrent] = useState(null);
  const timer = useRef(null);

  const searchFirms = (term) => {
    setQ(term);
    clearTimeout(timer.current);
    if (term.trim().length < 3) { setOptions([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/voters/paged", { params: { search: term.trim(), page: 1, page_size: 12 } });
        setOptions(data.items || data.rows || []);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  };

  const load = async (voter, typeList = types) => {
    setOptions([]);
    setQ(voter.company_name);
    setCurrent(voter);
    setLoading(true);
    try {
      const { data } = await api.get("/opportunities/network", {
        params: { voter_id: voter.id, depth: 2, ...(typeList.length ? { types: typeList.join(",") } : {}) },
      });
      setData(data);
      if (!data.total) toast.info("Bu firma için bağlantı bulunamadı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bağlantılar getirilemedi");
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (t) => {
    const next = types.includes(t) ? types.filter((x) => x !== t) : [...types, t];
    setTypes(next);
    if (current) load(current, next);
  };

  const download = async () => {
    try {
      const res = await api.get("/opportunities/network/export", {
        params: { voter_id: data.root.voter_id, depth: 2, ...(types.length ? { types: types.join(",") } : {}) }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "baglanti_agi.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarılamadı");
    }
  };

  const rows = useMemo(
    () => (data ? data.nodes.filter((n) => n.depth > 0).sort((a, b) => a.depth - b.depth || a.company_name.localeCompare(b.company_name, "tr")) : []),
    [data]
  );
  const fnT = useTableSort(rows);

  return (
    <div className="space-y-4" data-testid="firm-network">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Share2 className="h-4 w-4" style={{ color: NAVY }} />
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Firma Bağlantı Ağı</p>
        </div>
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Firma adı veya sicil no yazın (en az 3 harf)..." value={q}
            onChange={(e) => searchFirms(e.target.value)} data-testid="fn-search" />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
          {options.length > 0 && (
            <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg" data-testid="fn-options">
              {options.map((o) => (
                <button key={o.id} className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                  onClick={() => load(o)} data-testid={`fn-option-${o.id}`}>
                  <span className="font-medium">{o.company_name}</span>
                  <span className="text-muted-foreground"> · #{o.committee_no ?? "—"} · {o.branch || "Merkez"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Seçilen firmanın yetkili/ortakları, telefonu, adresi ve unvan/sicil kökü üzerinden 2 adıma kadar bağlı firmalar çıkarılır.
        </p>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="fn-type-filter">
          <span className="text-xs text-muted-foreground mr-1">Bağlantı türü:</span>
          <Button type="button" size="sm" variant={types.length === 0 ? "default" : "outline"}
            className={`h-7 text-xs ${types.length === 0 ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""}`}
            onClick={() => { setTypes([]); if (current) load(current, []); }} data-testid="fn-type-all">
            Tümü
          </Button>
          {Object.entries(REASON).map(([k, meta]) => {
            const Icon = meta.icon;
            const on = types.includes(k);
            return (
              <Button key={k} type="button" size="sm" variant={on ? "default" : "outline"}
                className={`h-7 text-xs ${on ? "bg-[#0F2C59] hover:bg-[#0F2C59]/90" : ""}`}
                onClick={() => toggleType(k)} data-testid={`fn-type-${k}`}>
                <Icon className="h-3 w-3 mr-1" />{meta.l}
              </Button>
            );
          })}
        </div>
      </Card>

      {loading && <Card className="p-10 text-center text-sm text-muted-foreground">Bağlantılar çıkarılıyor...</Card>}

      {data && !loading && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className="text-xs" data-testid="fn-root">{data.root.company_name}</Badge>
            <Badge className="text-xs" style={{ background: PRED_COLOR[data.root.prediction] }}>
              {PRED_LABEL[data.root.prediction]}
            </Badge>
            <span className="text-xs text-muted-foreground" data-testid="fn-total">
              {data.total} bağlantılı firma · 1. adım {data.nodes.filter((n) => n.depth === 1).length} · 2. adım {data.nodes.filter((n) => n.depth === 2).length}
            </span>
            {canExport && (
              <Button variant="outline" size="sm" className="ml-auto" onClick={download} data-testid="fn-export">
                <Download className="h-3.5 w-3.5 mr-1" /> Excel
              </Button>
            )}
          </div>

          <Graph data={data} onOpenFirm={onOpenFirm} />

          {data.suggestions.length > 0 && (
            <Card className="p-4 space-y-2" data-testid="fn-suggestions">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                <p className="text-sm font-semibold" style={{ color: NAVY }}>Ulaşım yolu önerileri</p>
                <Badge variant="outline" className="text-xs">{data.suggestions.length}</Badge>
              </div>
              <div className="space-y-1.5">
                {data.suggestions.map((s, i) => (
                  <div key={i} className="text-xs rounded-lg border border-border/70 px-3 py-2 hover:bg-muted/40 cursor-pointer" data-testid={`fn-sug-${i}`}
                    onClick={() => onOpenFirm?.(s.voter_id)}>
                    <b>{s.company_name}</b> ({PRED_LABEL[s.prediction]}) firmasına <b>{s.bridge_company}</b> üzerinden ulaşılabilir
                    {s.bridge_responsible ? ` · sorumlu: ${s.bridge_responsible}` : ""}
                    {s.bridge_phone ? ` · ${s.bridge_phone}` : ""}
                    <div className="mt-1"><ReasonChips reasons={s.reasons} /></div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="fn-table">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <fnT.Th sortKey="fn-adim" get={(r) => r.depth ?? 0} lowBest className="text-left font-medium py-2 px-3">Adım</fnT.Th>
                  <fnT.Th sortKey="fn-firma" get={(r) => r.company_name} text className="text-left font-medium py-2 px-3">Firma</fnT.Th>
                  <fnT.Th sortKey="fn-komite" get={(r) => r.committee_no} className="text-left font-medium py-2 px-3">Komite / Şube</fnT.Th>
                  <fnT.Th sortKey="fn-2026" get={(r) => r.prediction || ""} text className="text-center font-medium py-2 px-3">2026</fnT.Th>
                  <fnT.Th sortKey="fn-belge" get={(r) => (r.proxy_document ? 1 : 0)} className="text-center font-medium py-2 px-3">Belge</fnT.Th>
                  <fnT.Th sortKey="fn-sorumlu" get={(r) => r.responsible} text className="text-left font-medium py-2 px-3">Sorumlu</fnT.Th>
                  <fnT.Th sortKey="fn-baglanti" get={(r) => (r.reasons || []).length} className="text-left font-medium py-2 px-3">Bağlantı</fnT.Th>
                </tr>
              </thead>
              <tbody>
                {fnT.sorted.map((n) => (
                  <tr key={n.voter_id} className="border-t border-border/50 hover:bg-muted/30 cursor-pointer" data-testid={`fn-row-${n.voter_id}`}
                    onClick={() => onOpenFirm?.(n.voter_id)}>
                    <td className="py-2 px-3 text-muted-foreground">{n.depth}</td>
                    <td className="py-2 px-3 font-medium underline decoration-dotted decoration-muted-foreground/50">{n.company_name}</td>
                    <td className="py-2 px-3 text-muted-foreground">#{n.committee_no ?? "—"} · {n.branch}</td>
                    <td className="py-2 px-3 text-center" style={{ color: PRED_COLOR[n.prediction] }}>{PRED_LABEL[n.prediction]}</td>
                    <td className="py-2 px-3 text-center">{n.proxy_document ? "Var" : "—"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{n.responsible || "—"}</td>
                    <td className="py-2 px-3">
                      <ReasonChips reasons={n.reasons} />
                      {n.via_name && n.depth === 2 && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">{n.via_name} üzerinden</div>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">Bağlantı bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
};
